import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import {
  createTicket, getMyTickets, getAllTickets, getTicket, addTicketComment, updateTicket,
} from "../api";
import { AsyncButton } from "../components/AsyncButton";
import { formatDate, formatDateTime } from "../utils/dateFormat";
import { useTenantTimezone } from "../hooks/useTenantTimezone";
import { LoadingOverlay } from "../components/LoadingOverlay";

const NAVY = "#1e3a8a";
const TEAL = "#00c9a7";

const CATEGORIES = [
  { value: "payroll",   label: "💰 Payroll" },
  { value: "leave",     label: "🌴 Leave" },
  { value: "benefits",  label: "🎁 Benefits" },
  { value: "it",        label: "💻 IT" },
  { value: "documents", label: "📄 Documents" },
  { value: "general",   label: "📋 General" },
];

const STATUS_META: Record<string, { bg: string; fg: string; label: string }> = {
  open:        { bg: "#e0f2fe", fg: "#0369a1", label: "Open" },
  in_progress: { bg: "#fef3c7", fg: "#b45309", label: "In Progress" },
  resolved:    { bg: "#dcfce7", fg: "#15803d", label: "Resolved" },
  closed:      { bg: "#f1f5f9", fg: "#64748b", label: "Closed" },
};

function StatusChip({ status }: { status: string }) {
  const m = STATUS_META[status] || STATUS_META.open;
  return <span style={{ background: m.bg, color: m.fg, padding: "3px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600 }}>{m.label}</span>;
}

export default function HelpdeskPage() {
  const { user } = useAuth();
  const tz = useTenantTimezone();
  const { toast } = useToast();
  const isHR = ["admin", "admin_assistant", "hr"].includes(user?.role || "");

  const [tab, setTab] = useState<"my" | "all">("my");
  const [statusFilter, setStatusFilter] = useState("");
  const [tickets, setTickets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // New ticket modal
  const [showNew, setShowNew] = useState(false);
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("general");
  const [priority, setPriority] = useState("normal");
  const [saving, setSaving] = useState(false);

  // Detail panel
  const [detail, setDetail] = useState<any | null>(null);
  const [comment, setComment] = useState("");
  const [commenting, setCommenting] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);

  async function load() {
    setLoading(true);
    try {
      if (tab === "all" && isHR) {
        setTickets(await getAllTickets(statusFilter ? { status: statusFilter } : undefined));
      } else {
        setTickets(await getMyTickets());
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [tab, statusFilter]);

  async function handleCreate() {
    if (!subject.trim()) { toast("Subject is required", "error"); return; }
    setSaving(true);
    try {
      await createTicket({ subject, description, category, priority });
      toast("Ticket created — HR has been notified", "success");
      setShowNew(false); setSubject(""); setDescription(""); setCategory("general"); setPriority("normal");
      load();
    } catch { toast("Failed to create ticket", "error"); }
    finally { setSaving(false); }
  }

  async function openDetail(id: number) {
    try { setDetail(await getTicket(id)); }
    catch { toast("Failed to load ticket", "error"); }
  }

  async function handleComment() {
    if (!comment.trim() || !detail) return;
    setCommenting(true);
    try {
      await addTicketComment(detail.id, comment.trim());
      setComment("");
      openDetail(detail.id);
    } catch { toast("Failed to add comment", "error"); }
    finally { setCommenting(false); }
  }

  async function changeStatus(status: string) {
    if (!detail) return;
    setChangingStatus(true);
    try {
      await updateTicket(detail.id, { status });
      toast(`Ticket ${STATUS_META[status]?.label.toLowerCase() || status}`, "success");
      openDetail(detail.id);
      load();
    } catch { toast("Failed to update", "error"); }
    finally { setChangingStatus(false); }
  }

  return (
    <div>
      {/* Width and padding come from ShellMain. This is a list page like
          Candidates, Jobs, Offers and Talent Pool — all full width — so a
          cap here left it visibly narrower than its siblings, and its own
          padding stacked on the shell's. */}
      <LoadingOverlay active={saving || commenting || changingStatus} />
      {/* The house `.page-header` block. A hand-rolled navy <h2> here read as a
          different kind of heading from the black <h1> every sibling page uses. */}
      <div className="page-header">
        <div>
          <h1>HR Helpdesk</h1>
          <p>Questions about payroll, leave, benefits — raise a ticket</p>
        </div>
        <button onClick={() => setShowNew(true)}
          style={{ background: `linear-gradient(135deg, ${NAVY}, ${TEAL})`, color: "white", border: "none", padding: "10px 20px", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
          + New Ticket
        </button>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
        {isHR && (
          <>
            <button onClick={() => setTab("my")} style={{
              padding: "8px 18px", borderRadius: 8, border: "none", cursor: "pointer", fontWeight: 600, fontSize: 13,
              background: tab === "my" ? NAVY : "#f1f5f9", color: tab === "my" ? "white" : "#475569",
            }}>My Tickets</button>
            <button onClick={() => setTab("all")} style={{
              padding: "8px 18px", borderRadius: 8, border: "none", cursor: "pointer", fontWeight: 600, fontSize: 13,
              background: tab === "all" ? NAVY : "#f1f5f9", color: tab === "all" ? "white" : "#475569",
            }}>All Tickets</button>
          </>
        )}
        {tab === "all" && (
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            style={{ padding: "8px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 13 }}>
            <option value="">All statuses</option>
            {Object.entries(STATUS_META).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}
          </select>
        )}
      </div>

      {loading ? (
        <div style={{ padding: 60, textAlign: "center", color: "#94a3b8" }}>Loading…</div>
      ) : tickets.length === 0 ? (
        <div data-testid="helpdesk-empty" style={{ textAlign: "center", padding: 60, color: "#94a3b8", background: "#f8fafc", borderRadius: 12 }}>
          <div style={{ fontSize: 40 }}>🎫</div>
          <div style={{ marginTop: 10 }}>{tab === "all" ? "No tickets found." : "No tickets yet. Raise one if you need help from HR."}</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {tickets.map(t => (
            <div key={t.id} data-testid="helpdesk-ticket" onClick={() => openDetail(t.id)} style={{
              background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: "14px 18px",
              display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", flexWrap: "wrap", gap: 8,
            }}>
              <div>
                <div style={{ fontWeight: 600, color: "#0f172a" }}>
                  #{t.id} · {t.subject}
                  {t.priority === "high" && <span style={{ color: "#dc2626", fontSize: 11, fontWeight: 700, marginLeft: 8 }}>HIGH</span>}
                </div>
                <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>
                  {CATEGORIES.find(c => c.value === t.category)?.label || t.category}
                  {tab === "all" && t.employee_name ? ` · ${t.employee_name}` : ""}
                  {" · "}{formatDate(t.created_at, "short", tz)}
                </div>
              </div>
              <StatusChip status={t.status} />
            </div>
          ))}
        </div>
      )}

      {/* ── New ticket modal ── */}
      {showNew && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "white", borderRadius: 14, padding: 28, maxWidth: 480, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <h3 style={{ margin: "0 0 16px", color: NAVY }}>New Helpdesk Ticket</h3>
            <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject — what do you need help with?"
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 14, marginBottom: 12, boxSizing: "border-box" }} />
            <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
              <select value={category} onChange={e => setCategory(e.target.value)}
                style={{ flex: 1, padding: "10px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 13 }}>
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              <select value={priority} onChange={e => setPriority(e.target.value)}
                style={{ flex: 1, padding: "10px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 13 }}>
                <option value="low">Low priority</option>
                <option value="normal">Normal priority</option>
                <option value="high">High priority</option>
              </select>
            </div>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={4} placeholder="Describe the issue…"
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 14, marginBottom: 18, resize: "vertical", boxSizing: "border-box" }} />
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setShowNew(false)}
                style={{ background: "#f1f5f9", color: "#475569", border: "none", padding: "9px 18px", borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Cancel</button>
              <button disabled={saving} onClick={handleCreate}
                style={{ background: NAVY, color: "white", border: "none", padding: "9px 20px", borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                {saving ? "Creating…" : "Create Ticket"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Detail panel ── */}
      {detail && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
          onClick={() => setDetail(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: "white", borderRadius: 14, padding: 28, maxWidth: 560, width: "100%", maxHeight: "85vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
              <h3 style={{ margin: 0, color: NAVY }}>#{detail.id} · {detail.subject}</h3>
              <button onClick={() => setDetail(null)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94a3b8" }}>×</button>
            </div>
            <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 12 }}>
              {CATEGORIES.find(c => c.value === detail.category)?.label || detail.category}
              {detail.employee_name ? ` · raised by ${detail.employee_name}` : ""}
              {" · "}{formatDateTime(detail.created_at, tz)}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
              <StatusChip status={detail.status} />
              {isHR && detail.status !== "closed" && (
                <>
                  {detail.status === "open" && (
                    <AsyncButton onClick={() => changeStatus("in_progress")} style={{ background: "#fef3c7", color: "#b45309", border: "none", padding: "5px 12px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>Start Progress</AsyncButton>
                  )}
                  {detail.status !== "resolved" && (
                    <AsyncButton onClick={() => changeStatus("resolved")} style={{ background: "#dcfce7", color: "#15803d", border: "none", padding: "5px 12px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>Mark Resolved</AsyncButton>
                  )}
                  <AsyncButton onClick={() => changeStatus("closed")} style={{ background: "#f1f5f9", color: "#64748b", border: "none", padding: "5px 12px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>Close</AsyncButton>
                </>
              )}
              {!isHR && detail.user_id === user?.id && detail.status !== "closed" && (
                <AsyncButton onClick={() => changeStatus("closed")} style={{ background: "#f1f5f9", color: "#64748b", border: "none", padding: "5px 12px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>Close Ticket</AsyncButton>
              )}
            </div>
            {detail.description && (
              <div style={{ background: "#f8fafc", borderRadius: 10, padding: 14, fontSize: 14, color: "#334155", marginBottom: 18, whiteSpace: "pre-wrap" }}>
                {detail.description}
              </div>
            )}

            <h4 style={{ fontSize: 13, color: "#64748b", margin: "0 0 10px", textTransform: "uppercase", letterSpacing: "0.05em" }}>Conversation</h4>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
              {(detail.comments || []).length === 0 && (
                <div style={{ fontSize: 13, color: "#94a3b8" }}>No replies yet.</div>
              )}
              {(detail.comments || []).map((c: any) => (
                <div key={c.id} style={{
                  background: c.user_id === user?.id ? "#eff6ff" : "#f8fafc",
                  borderRadius: 10, padding: "10px 14px",
                }}>
                  <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4 }}>
                    <strong style={{ color: "#475569" }}>{c.user_name || "User"}</strong> · {formatDateTime(c.created_at, tz)}
                  </div>
                  <div style={{ fontSize: 13, color: "#334155", whiteSpace: "pre-wrap" }}>{c.body}</div>
                </div>
              ))}
            </div>

            {detail.status !== "closed" && (
              <div style={{ display: "flex", gap: 8 }}>
                <input value={comment} onChange={e => setComment(e.target.value)} placeholder="Write a reply…"
                  onKeyDown={e => { if (e.key === "Enter") handleComment(); }}
                  style={{ flex: 1, padding: "10px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 13 }} />
                <button disabled={commenting || !comment.trim()} onClick={handleComment}
                  style={{ background: NAVY, color: "white", border: "none", padding: "10px 18px", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                  Send
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
